'use client';

import { useState } from 'react';

import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@kit/ui/drawer';
import { LoadingOverlay } from '@kit/ui/loading-overlay';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { useMyMemberships } from '~/(tenant)/_components/native/_lib/use-my-memberships';
import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import Link from '~/(tenant)/_components/tenant-link';
import type { MyMembershipRow } from '~/(tenant)/memberships/_lib/server/memberships-page.loader';

import type { MembershipStatus } from '../member-id-card-section';
import {
  MemberStatusBadge,
  QrSurface,
  StatusBanner,
  useRotatingQrPayload,
} from './rotating-qr-card';

type CardStatus = Exclude<MembershipStatus, 'cancelled'>;

/**
 * Bottom sheet behind the home "Check in" quick action: the member's live
 * rotating QR, name and member number. Parents with linked children can
 * switch between each member's code. Non-active memberships show why the
 * code is unavailable instead of a QR.
 */
export function CheckInSheet({
  accountId,
  open,
  onOpenChange,
}: {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const presentation = useNativePresentation();

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-test="check-in-sheet"
        className={cn(presentation.className, 'mx-auto max-w-lg')}
        style={presentation.style}
      >
        <DrawerHeader className="relative px-6 pe-16 text-center">
          <DrawerTitle className="text-center">
            <Trans i18nKey="common:checkInSheet.title" defaults="Check in" />
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            <Trans
              i18nKey="common:nativeHome.memberIdCard.dialogDescription"
              defaults="Show this code to reception. The code refreshes every 30 seconds."
            />
          </DrawerDescription>
          <DrawerClose asChild>
            <Button
              variant="outline"
              size="icon"
              className="absolute end-4 top-2 h-11 w-11 rounded-full"
              data-test="check-in-sheet-close"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">
                <Trans i18nKey="common:close" defaults="Close" />
              </span>
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="overflow-y-auto px-6 pt-2 pb-10">
          {open ? <CheckInSheetBody accountId={accountId} /> : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function CheckInSheetBody({ accountId }: { accountId: string }) {
  const { userId, isSessionPending, memberships, isPending, isError } =
    useMyMemberships(accountId);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (isSessionPending || isPending) {
    return (
      <div className="relative h-72">
        <LoadingOverlay fullPage={false} />
      </div>
    );
  }

  if (!userId) {
    return (
      <SheetCta
        href="/auth/sign-in?next=/"
        dataTest="check-in-sheet-sign-in"
        message={
          <Trans
            i18nKey="common:checkInSheet.signedOut"
            defaults="Sign in to show your member code."
          />
        }
        label={
          <Trans i18nKey="common:checkInSheet.signIn" defaults="Sign in" />
        }
      />
    );
  }

  if (isError) {
    return (
      <SheetMessage>
        <Trans
          i18nKey="common:checkInSheet.loadError"
          defaults="We couldn't load your member code. Please try again."
        />
      </SheetMessage>
    );
  }

  const candidates = sortCandidates(
    (memberships ?? []).filter((m) => m.status !== 'cancelled'),
  );

  if (candidates.length === 0) {
    return <NoMembership />;
  }

  const selected =
    candidates.find((m) => m.id === selectedId) ?? candidates[0]!;

  return (
    <div className="flex flex-col items-center gap-5">
      {candidates.length > 1 ? (
        <MemberSwitcher
          candidates={candidates}
          selectedId={selected.id}
          onSelect={setSelectedId}
        />
      ) : null}
      <MemberCode membership={selected} />
    </div>
  );
}

// Own membership before children's; active before anything else.
function sortCandidates(rows: MyMembershipRow[]) {
  const rank = (m: MyMembershipRow) =>
    (m.is_child ? 2 : 0) + (m.status === 'active' ? 0 : 1);
  return [...rows].sort((a, b) => rank(a) - rank(b));
}

function MemberCode({ membership }: { membership: MyMembershipRow }) {
  const t = useNativeTokens();
  const status = membership.status as CardStatus;
  const isActive = status === 'active';
  const payload = useRotatingQrPayload(
    membership.id,
    membership.qr_secret,
    isActive,
  );
  const canShowCode = isActive && Boolean(membership.qr_secret);
  const name = membership.member_name?.trim() || membership.member_number || '';

  return (
    <>
      <QrSurface payload={payload} size={240} disabled={!canShowCode} />

      <div className="flex flex-col items-center gap-1 text-center">
        <div
          data-test="check-in-member-name"
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 22,
            fontWeight: 600,
            color: t.foreground,
          }}
        >
          {name}
        </div>
        <div style={{ fontFamily: t.fontSans, fontSize: 14, color: t.muted }}>
          {[membership.plan_name, membership.member_number]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {!isActive ? (
          <div className="mt-2">
            <MemberStatusBadge status={status} />
          </div>
        ) : null}
      </div>

      {isActive ? (
        <p
          className="text-center"
          style={{ fontFamily: t.fontSans, fontSize: 12, color: t.muted }}
        >
          <Trans
            i18nKey="common:nativeHome.memberIdCard.refreshHint"
            defaults="Refreshes every 30 seconds for security."
          />
        </p>
      ) : (
        <div className="w-full">
          <StatusBanner status={status as Exclude<CardStatus, 'active'>} />
        </div>
      )}
    </>
  );
}

function MemberSwitcher({
  candidates,
  selectedId,
  onSelect,
}: {
  candidates: MyMembershipRow[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();

  const nameOf = (m: MyMembershipRow) => m.member_name?.trim() || '';
  const nameCounts = new Map<string, number>();
  for (const m of candidates) {
    nameCounts.set(nameOf(m), (nameCounts.get(nameOf(m)) ?? 0) + 1);
  }
  // One person on two plans would otherwise get two identical chips.
  const labelOf = (m: MyMembershipRow) => {
    const name = nameOf(m);
    if (!name) return m.plan_name;
    return (nameCounts.get(name) ?? 0) > 1 ? `${name} · ${m.plan_name}` : name;
  };

  return (
    <div
      role="group"
      aria-label={translate('common:checkInSheet.chooseMember', {
        defaultValue: 'Choose member',
      })}
      className="flex max-w-full gap-2 overflow-x-auto pb-1"
    >
      {candidates.map((membership) => {
        const selected = membership.id === selectedId;
        return (
          <button
            key={membership.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(membership.id)}
            className="shrink-0"
            style={{
              minHeight: 44,
              padding: '6px 14px',
              borderRadius: 999,
              border: `1px solid ${selected ? t.foreground : t.border}`,
              backgroundColor: selected ? t.foreground : 'transparent',
              color: selected ? t.background : t.foreground,
              fontFamily: t.fontSans,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {labelOf(membership)}
          </button>
        );
      })}
    </div>
  );
}

function NoMembership() {
  return (
    <SheetCta
      href="/memberships"
      dataTest="check-in-sheet-join"
      message={
        <Trans
          i18nKey="common:checkInSheet.noMembership"
          defaults="Check-in codes come with a membership."
        />
      }
      label={
        <Trans
          i18nKey="common:checkInSheet.viewPlans"
          defaults="View membership plans"
        />
      }
    />
  );
}

function SheetCta({
  href,
  dataTest,
  message,
  label,
}: {
  href: string;
  dataTest: string;
  message: React.ReactNode;
  label: React.ReactNode;
}) {
  const t = useNativeTokens();

  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <SheetMessage>{message}</SheetMessage>
      <Link
        href={href}
        data-test={dataTest}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 44,
          padding: '0 24px',
          borderRadius: 999,
          backgroundColor: t.accent,
          color: t.background,
          fontFamily: t.fontSans,
          fontSize: 15,
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        {label}
      </Link>
    </div>
  );
}

function SheetMessage({ children }: { children: React.ReactNode }) {
  const t = useNativeTokens();

  return (
    <p
      style={{
        fontFamily: t.fontSans,
        fontSize: 15,
        color: t.muted,
        maxWidth: 300,
        margin: '0 auto',
        textAlign: 'center',
      }}
    >
      {children}
    </p>
  );
}
